import type { AutoReplyRule, TriggerType } from '@/api/auto-replies';

export type AutoReplySectionId =
  | 'confirmations'
  | 'feedback'
  | 'care_followup'
  | 'birthday'
  | 'marketing_engagement'
  | 'account_engagement'
  | 'post_procedure'
  | 'all';

export interface AutoReplyContextOption {
  value: string;
  label: string;
  description: string;
}

export interface AutoReplySectionConfig {
  id: AutoReplySectionId;
  label: string;
  description: string;
  contexts: AutoReplyContextOption[];
}

export const autoReplySectionRegistry: AutoReplySectionConfig[] = [
  {
    id: 'confirmations',
    label: 'Подтверждения',
    description: 'Подтверждение, перенос и отмена после reminder/change/cancel сценариев.',
    contexts: [
      { value: 'appointment_reminder_advance', label: 'Напоминание заранее', description: 'Пациент отвечает после advance-reminder.' },
      { value: 'appointment_reminder_24h', label: 'Напоминание за 24 часа', description: 'Контекст для подтверждений/переноса/отмены после 24h reminder.' },
      { value: 'appointment_reminder_2h', label: 'Напоминание за 2 часа', description: 'Контекст для ответов на 2h reminder.' },
      { value: 'appointment_reminder', label: 'Legacy reminder', description: 'Нужен только для старых исходящих без dedicated event_type.' },
      { value: 'appointment_changed', label: 'Изменение записи', description: 'Ответ после change-сценария.' },
      { value: 'appointment_cancelled', label: 'Отмена записи', description: 'Ответ после cancel-сценария.' },
    ],
  },
  {
    id: 'feedback',
    label: 'Сбор ОС',
    description: 'Оценки, отзывы и follow-up после feedback request.',
    contexts: [
      { value: 'feedback_request', label: 'Сбор обратной связи', description: 'Контекст после запроса оценки или отзыва.' },
    ],
  },
  {
    id: 'care_followup',
    label: 'После приёма',
    description: 'Правила после завершённого приёма и recommendation/care сценариев.',
    contexts: [
      { value: 'appointment_completed', label: 'Приём завершён', description: 'После completed appointment сценария.' },
    ],
  },
  {
    id: 'birthday',
    label: 'ДР',
    description: 'Birthday/gift сценарии и ответы на поздравления.',
    contexts: [
      { value: 'birthday', label: 'День рождения', description: 'Контекст для birthday broadcast и related CTA.' },
    ],
  },
  {
    id: 'marketing_engagement',
    label: 'Маркетинг',
    description: 'Маркетинговые офферы, custom trigger и promo CTA.',
    contexts: [
      { value: 'custom_trigger', label: 'Маркетинговый/custom trigger', description: 'Используйте для кастомных офферов и промо-рассылок.' },
    ],
  },
  {
    id: 'account_engagement',
    label: 'Личный кабинет',
    description: 'Переходы в ЛК и сценарии contact hub / account CTA.',
    contexts: [
      { value: 'personal_account', label: 'Личный кабинет', description: 'Контекст для account-related CTA и ссылок в ЛК.' },
      { value: 'contact_hub', label: 'Каналы связи / contact hub', description: 'Контекст для OKDoc landing с каналами связи и кросс-CTA.' },
    ],
  },
  {
    id: 'post_procedure',
    label: 'После процедур',
    description: 'Постпроцедурные и service-completed сценарии.',
    contexts: [
      { value: 'service_completed', label: 'Услуга завершена', description: 'Контекст для post-procedure follow-up.' },
    ],
  },
  {
    id: 'all',
    label: 'Все ответы',
    description: 'Общие catch-all правила без привязки к конкретному outbound-сценарию.',
    contexts: [],
  },
];

const SECTION_BY_CONTEXT: Record<string, AutoReplySectionId> = {
  appointment_reminder: 'confirmations',
  appointment_reminder_24h: 'confirmations',
  appointment_reminder_2h: 'confirmations',
  appointment_reminder_advance: 'confirmations',
  appointment_changed: 'confirmations',
  appointment_cancelled: 'confirmations',
  feedback_request: 'feedback',
  feedback_positive: 'feedback',
  feedback_negative: 'feedback',
  appointment_completed: 'care_followup',
  birthday: 'birthday',
  custom_trigger: 'marketing_engagement',
  marketing: 'marketing_engagement',
  promo_offer: 'marketing_engagement',
  personal_account: 'account_engagement',
  contact_hub: 'account_engagement',
  contact_navigation: 'account_engagement',
  service_completed: 'post_procedure',
};

function normalizeValue(value?: string | null): string {
  return (value || '').trim().toLowerCase();
}

function ruleSignals(rule: Partial<AutoReplyRule>): string[] {
  const values = [
    ...(rule.waba_button_ids || []),
    ...(rule.tg_callback_data || []),
    rule.pattern || '',
    rule.name || '',
    rule.description || '',
  ];
  return values.map((value) => normalizeValue(value)).filter(Boolean);
}

function someSignal(rule: Partial<AutoReplyRule>, markers: string[]): boolean {
  const signals = ruleSignals(rule);
  return signals.some((signal) => markers.some((marker) => signal.includes(marker)));
}

export function getAutoReplySectionConfig(sectionId: AutoReplySectionId): AutoReplySectionConfig {
  return autoReplySectionRegistry.find((section) => section.id === sectionId) || autoReplySectionRegistry[0];
}

export function getAutoReplyContextLabel(afterEventType?: string): string | null {
  const normalized = normalizeValue(afterEventType);
  if (!normalized) {
    return null;
  }

  for (const section of autoReplySectionRegistry) {
    const option = section.contexts.find((item) => item.value === normalized);
    if (option) {
      return option.label;
    }
  }

  return afterEventType || null;
}

export function inferAutoReplySection(rule: Partial<AutoReplyRule>): AutoReplySectionId {
  const context = normalizeValue(rule.after_event_type);
  if (context && SECTION_BY_CONTEXT[context]) {
    return SECTION_BY_CONTEXT[context];
  }

  if (rule.trigger_type === 'feedback') {
    return 'feedback';
  }

  if (someSignal(rule, ['confirm_', 'приду', 'перенести', 'не приду', 'cancel', 'reschedule'])) {
    return 'confirmations';
  }

  if (someSignal(rule, ['feedback_', 'отзыв', 'оцен', 'review'])) {
    return 'feedback';
  }

  if (someSignal(rule, ['birthday', 'день рождения', 'др', 'gift', 'подар'])) {
    return 'birthday';
  }

  if (someSignal(rule, ['personal_account', 'личный кабинет', 'lk_', 'contact_hub', 'contact_navigation'])) {
    return 'account_engagement';
  }

  if (someSignal(rule, ['service_completed', 'post_procedure', 'процедур'])) {
    return 'post_procedure';
  }

  if (someSignal(rule, ['appointment_completed', 'recommendation', 'care_followup'])) {
    return 'care_followup';
  }

  if (someSignal(rule, ['promo', 'offer', 'marketing', 'акци', 'custom_trigger'])) {
    return 'marketing_engagement';
  }

  return 'all';
}

export function getAutoReplySectionBadgeClass(sectionId: AutoReplySectionId): string {
  switch (sectionId) {
    case 'confirmations':
      return 'bg-emerald-100 text-emerald-700';
    case 'feedback':
      return 'bg-cyan-100 text-cyan-700';
    case 'care_followup':
      return 'bg-blue-100 text-blue-700';
    case 'birthday':
      return 'bg-pink-100 text-pink-700';
    case 'marketing_engagement':
      return 'bg-orange-100 text-orange-700';
    case 'account_engagement':
      return 'bg-violet-100 text-violet-700';
    case 'post_procedure':
      return 'bg-sky-100 text-sky-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

export function getRecommendedTriggerTypes(sectionId: AutoReplySectionId): TriggerType[] {
  switch (sectionId) {
    case 'confirmations':
      return ['keyword', 'regex', 'waba_button', 'tg_callback'];
    case 'feedback':
      return ['feedback', 'regex', 'waba_button', 'tg_callback'];
    case 'care_followup':
    case 'birthday':
    case 'marketing_engagement':
    case 'account_engagement':
    case 'post_procedure':
      return ['keyword', 'regex', 'waba_button', 'tg_callback'];
    default:
      return ['first_message', 'keyword', 'regex', 'inbound_first_contact'];
  }
}
